'use client';

import { useEffect, useRef, useState } from 'react';
import { Rive } from '@rive-app/canvas';

type RiveComponentProps = {
  src: string;
  autoplay?: boolean;
  playAudio?: boolean;
  width?: number;
  height?: number;
  className?: string;
};

const RiveComponent = ({
  src,
  autoplay = true,
  playAudio = false,
  width = 300,
  height = 300,
  className,
}: RiveComponentProps) => {
  const normalizedSrc = src.startsWith('//') ? `https:${src}` : src;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const riveRef = useRef<Rive | null>(null);
  const [detectedSMs, setDetectedSMs] = useState<string[] | null>(null);

  // Phase 1: Fetch the file and probe for state machine names
  useEffect(() => {
    let cancelled = false;

    fetch(normalizedSrc)
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        if (cancelled) return;
        // Create a throwaway offscreen canvas to probe state machine names
        const offscreen = document.createElement('canvas');
        offscreen.width = 1;
        offscreen.height = 1;
        const probe = new Rive({
          buffer: buffer as ArrayBuffer,
          canvas: offscreen,
          autoplay: false,
          onLoad: () => {
            try {
              const artboard = probe.contents?.artboards?.[0];
              const names = artboard?.stateMachines?.map((sm: { name: string }) => sm.name) ?? [];
              if (!cancelled) setDetectedSMs(names.length > 0 ? names : []);
            } catch {
              if (!cancelled) setDetectedSMs([]);
            }
            probe.cleanup();
          },
        });
      })
      .catch(() => {
        if (!cancelled) setDetectedSMs([]);
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedSrc]);

  // Phase 2: Create the real Rive instance once we know which state machines to use
  const resolvedSMs = detectedSMs;

  useEffect(() => {
    // Wait until we know the state machines (explicit or detected)
    if (resolvedSMs === null || resolvedSMs === undefined) return;
    if (!canvasRef.current) return;

    if (riveRef.current) {
      try {
        riveRef.current.cleanup();
      } catch {}
      riveRef.current = null;
    }

    const smOption = resolvedSMs.length > 0 ? { stateMachines: resolvedSMs } : {};

    const r = new Rive({
      src: normalizedSrc,
      canvas: canvasRef.current,
      autoplay,
      ...smOption,
      automaticallyHandleEvents: true,
      onLoad: () => {
        r.resizeDrawingSurfaceToCanvas();
        if (!playAudio) r.volume = 0;
      },
    });

    riveRef.current = r;

    const handleResize = () => {
      try {
        riveRef.current?.resizeDrawingSurfaceToCanvas();
      } catch {}
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (riveRef.current) {
        try {
          riveRef.current.cleanup();
        } catch {}
        riveRef.current = null;
      }
    };
  }, [normalizedSrc, resolvedSMs, autoplay, playAudio]);

  return <canvas ref={canvasRef} width={width} height={height} className={className} />;
};

export default RiveComponent;
